import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Collapse,
  IconButton,
  useTheme,
} from '@mui/material';
import {
  Print as PrintIcon,
  KeyboardArrowDown as KeyboardArrowDownIcon,
  KeyboardArrowUp as KeyboardArrowUpIcon,
  UnfoldMore as UnfoldMoreIcon,
  UnfoldLess as UnfoldLessIcon,
} from '@mui/icons-material';
import { format } from 'date-fns';
import { DailyOrderReport } from '../../types';

interface DailyOrdersReportProps {
  dailyReports: DailyOrderReport[];
  startDate: Date | null;
  endDate: Date | null;
  selectedBranchName: string;
}

const DailyOrdersReport: React.FC<DailyOrdersReportProps> = ({
  dailyReports,
  startDate,
  endDate,
  selectedBranchName,
}) => {
  const theme = useTheme();
  const [openRows, setOpenRows] = useState<Record<string, boolean>>({});

  // Reset row expansion state when the data set changes
  useEffect(() => {
    setOpenRows({});
  }, [dailyReports]);

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'EGP' }).format(amount);
  };

  const formatDateShort = (dateString: string) => {
    return format(new Date(dateString), 'MMM dd, yyyy');
  };

  const handlePrint = () => {
    window.print();
  };

  const toggleRow = (key: string) => {
    setOpenRows((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  // Compute keys for current rows and whether all are open
  const rowKeys = dailyReports.map((report, idx) => `${report.date ?? 'na'}-${idx}`);
  const allOpen = rowKeys.length > 0 && rowKeys.every((k) => !!openRows[k]);

  const handleToggleAll = () => {
    if (rowKeys.length === 0) return;
    setOpenRows((prev) => {
      const next: Record<string, boolean> = { ...prev };
      if (allOpen) {
        rowKeys.forEach((k) => { next[k] = false; });
      } else {
        rowKeys.forEach((k) => { next[k] = true; });
      }
      return next;
    });
  };

  if (dailyReports.length === 0) {
    return null;
  }

  return (
    <div className="daily-orders-report-container">
      <style>
        {`
          /* Container styling */
          .daily-orders-report-container {
            background: ${theme.palette.background.paper};
            padding: 0;
            margin: 0;
          }
          
          .print-header {
            border-bottom: 2px solid ${theme.palette.text.primary};
            margin-bottom: 8px;
            padding: 2px 15px 5px 15px;
            page-break-inside: avoid;
          }
          
          .print-header h1 {
            font-size: 24px;
            font-weight: bold;
            color: ${theme.palette.text.primary};
            margin: 0;
            text-align: center;
          }
          
          .app-name {
            font-size: 16px;
            font-weight: 600;
            color: ${theme.palette.primary.main};
            margin-bottom: 5px;
            
            letter-spacing: 1px;
            text-align: left;
          }
          
          .report-filters {
            margin-bottom: 20px;
            page-break-inside: avoid;
          }
          
          .report-filters > div {
            display: flex !important;
            align-items: center;
            gap: 20px;
            padding: 5px 15px;
            margin: 0 15px 10px 15px;
            font-size: 14px;
          }
          
          @media print {
            /* Page setup with proper margins */
            @page {
              margin: 10mm 12mm;
              size: A4 portrait;
            }
            
            * {
              -webkit-print-color-adjust: exact !important;
              print-color-adjust: exact !important;
              color-adjust: exact !important;
              box-sizing: border-box !important;
            }
            
            html, body {
              background: white !important;
              color: black !important;
              margin: 0 !important;
              padding: 0 !important;
              width: 100% !important;
              height: auto !important;
              overflow: visible !important;
              position: relative !important;
            }
            
            /* Force all parent containers to allow overflow */
            body > *, 
            #root,
            #root > *,
            main {
              overflow: visible !important;
              height: auto !important;
              max-height: none !important;
              position: relative !important;
            }
            
            .print-header {
              margin-bottom: 5mm !important;
              border-bottom: 2px solid black !important;
              padding: 2mm !important;
              page-break-after: avoid !important;
              page-break-inside: avoid !important;
            }
            
            .print-header h1 {
              font-size: 18px !important;
              margin: 0 0 3mm 0 !important;
              text-align: center !important;
              font-weight: bold !important;
              color: black !important;
            }
            
            .print-header > div > div {
              display: flex !important;
              justify-content: space-between !important;
              align-items: center !important;
              margin-bottom: 2mm !important;
            }
            
            .print-header > div > div > div {
              font-size: 12px !important;
              font-weight: 600 !important;
              color: black !important;
            }
            
            .app-name {
              font-size: 12px !important;
              color: black !important;
            }
            
            .report-filters {
              display: block !important;
              margin-bottom: 3mm !important;
              page-break-inside: avoid;
            }
            
            .report-filters > div {
              display: flex !important;
              border-bottom: 1px solid #ccc !important;
              color: black !important;
              font-size: 11px !important;
              padding: 2mm 3mm !important;
              margin: 0 3mm 3mm 3mm !important;
            }
            
            .report-filters strong {
              color: black !important;
            }
            
            /* Print styles for container and table */
            .daily-orders-report-container {
              background: white !important;
              color: black !important;
            }
            
            /* Remove all link-like styling */
            * {
              text-decoration: none !important;
            }
            
            a, a:link, a:visited, a:hover, a:active {
              color: black !important;
              text-decoration: none !important;
            }
            
            /* Table print styles */
            table, .MuiTable-root {
              border-collapse: collapse !important;
              color: black !important;
              background: white !important;
              width: 100% !important;
              page-break-inside: auto !important;
            }
            
            th, td, .MuiTableCell-root {
              color: black !important;
              background: white !important;
              border: 1px solid #ccc !important;
              text-decoration: none !important;
              padding: 4px 8px !important;
            }
            
            thead, .MuiTableHead-root {
              display: table-header-group !important;
              page-break-inside: avoid !important;
              page-break-after: avoid !important;
            }
            
            th, .MuiTableCell-head {
              background-color: #f5f5f5 !important;
              font-weight: bold !important;
              page-break-inside: avoid !important;
              page-break-after: avoid !important;
            }
            
            tbody, .MuiTableBody-root {
              display: table-row-group !important;
            }
            
            tr {
              break-inside: avoid !important;
              page-break-inside: avoid !important;
              page-break-after: auto !important;
            }
            
            /* Prevent orphaned rows */
            tr:has(+ tr) {
              page-break-after: auto !important;
            }
            
            /* Hide expand/collapse icons */
            .MuiIconButton-root {
              display: none !important;
            }
            
            /* Expand all collapsed content for print */
            .MuiCollapse-root {
              display: block !important;
              height: auto !important;
              overflow: visible !important;
              visibility: visible !important;
              opacity: 1 !important;
            }
            
            .MuiCollapse-wrapper,
            .MuiCollapse-wrapperInner {
              display: block !important;
              height: auto !important;
              overflow: visible !important;
            }
            
            /* Ensure table container is visible and flows across pages */
            .daily-orders-report-container .MuiTableContainer-root {
              overflow: visible !important;
              height: auto !important;
              max-height: none !important;
              min-height: 0 !important;
              page-break-inside: auto !important;
              display: block !important;
              position: relative !important;
            }
            
            /* Ensure the main container flows properly */
            .daily-orders-report-container {
              overflow: visible !important;
              height: auto !important;
              max-height: none !important;
              min-height: 0 !important;
              position: relative !important;
              display: block !important;
            }
            
            /* Ensure Paper components don't restrict height */
            .daily-orders-report-container .MuiPaper-root {
              overflow: visible !important;
              height: auto !important;
              max-height: none !important;
              min-height: 0 !important;
              box-shadow: none !important;
              position: relative !important;
            }
            
            /* Ensure Box components allow content flow */
            .daily-orders-report-container .MuiBox-root {
              overflow: visible !important;
              height: auto !important;
              max-height: none !important;
              position: relative !important;
            }
            
            /* Container classes that might restrict height */
            .MuiContainer-root {
              overflow: visible !important;
              height: auto !important;
              max-height: none !important;
              position: relative !important;
            }
          }
        `}
      </style>
      
      {/* Header Section */}
      <div className="print-header">
        <div style={{ marginBottom: '10px' }}>
          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            marginBottom: '10px'
          }}>
            <div className="app-name" style={{ 
              fontSize: '16px', 
              fontWeight: '600', 
              color: theme.palette.primary.main,
              letterSpacing: '1px'
            }}>
              Dining-X
            </div>
            <div style={{ 
              fontSize: '16px', 
              fontWeight: '600', 
              color: theme.palette.text.primary,
              letterSpacing: '1px'
            }}>
              {dailyReports.length > 0 && dailyReports[0].restaurantName ? dailyReports[0].restaurantName : 'Restaurant Name'}
            </div>
          </div>
          <h1 style={{ fontWeight: 'bold', color: theme.palette.text.primary, margin: 0, textAlign: 'center' }}>
            Daily Orders Report
          </h1>
        </div>
      </div>

      {/* Filter Information */}
      <div className="report-filters">
        <div style={{ 
          display: 'flex',
          alignItems: 'center',
          gap: '20px',
          padding: '5px 15px',
          margin: '0 15px 10px 15px',
          fontSize: '14px',
          minHeight: 'auto',
          borderBottom: `1px solid ${theme.palette.divider}`,
          color: theme.palette.text.primary
        }}>
          <span><strong>Period:</strong> {startDate ? formatDateShort(startDate.toISOString()) : '-'} - {endDate ? formatDateShort(endDate.toISOString()) : '-'}</span>
          <span><strong>Branch:</strong> {selectedBranchName || 'All Branches'}</span>
          <span><strong>Currency:</strong> EGP</span>
        </div>
      </div>
      
      <Box className="no-print" sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mb: 2 }}>
        <Button
          startIcon={allOpen ? <UnfoldLessIcon /> : <UnfoldMoreIcon />}
          onClick={handleToggleAll}
          variant="outlined"
        >
          {allOpen ? 'Collapse All' : 'Expand All'}
        </Button>
        <Button
          startIcon={<PrintIcon />}
          onClick={handlePrint}
          variant="outlined"
        >
          Print
        </Button>
      </Box>
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Day</TableCell>
              <TableCell align="right">Total Orders</TableCell>
              <TableCell align="right">Cancelled Orders</TableCell>
              <TableCell align="right">Start Check #</TableCell>
              <TableCell align="right">End Check #</TableCell>
              <TableCell align="right">Total Talabat</TableCell>
              <TableCell align="right">Total Sales</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {dailyReports.map((report, idx) => {
              const rowKey = `${report.date ?? 'na'}-${idx}`;
              const isOpen = !!openRows[rowKey];
              return (
                <React.Fragment key={rowKey}>
                  <TableRow>
                    <TableCell component="th" scope="row">
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <IconButton 
                          size="small" 
                          onClick={() => toggleRow(rowKey)} 
                          aria-label={isOpen ? 'Collapse' : 'Expand'}
                        >
                          {isOpen ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                        </IconButton>
                        {report.date ?? '-'}
                      </Box>
                    </TableCell>
                    <TableCell align="right">{report.countOfAllOrders}</TableCell>
                    <TableCell align="right">{report.countOfCancelledOrders}</TableCell>
                    <TableCell align="right">{report.startCloudShortNumber ?? '-'}</TableCell>
                    <TableCell align="right">{report.endCloudShortNumber ?? '-'}</TableCell>
                    <TableCell align="right">{formatCurrency(Number(report.totalClouldProviderSales))}</TableCell>
                    <TableCell align="right">{formatCurrency(Number(report.totalSales))}</TableCell>
                  </TableRow>
                  {isOpen && (
                    <TableRow>
                      <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={7}>
                        <Box sx={{ m: 2 }}>
                          <Typography variant="subtitle2" gutterBottom>
                            Details for {report.date ?? '-'}
                          </Typography>
                          <Table size="small">
                            <TableHead>
                              <TableRow>
                                <TableCell>Category</TableCell>
                                <TableCell align="right">Count</TableCell>
                                <TableCell align="right">Amount</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              <TableRow>
                                <TableCell>Cash Orders</TableCell>
                                <TableCell align="right">{report.countOfCashOrders}</TableCell>
                                <TableCell align="right">{formatCurrency(Number(report.totalCashOrders))}</TableCell>
                              </TableRow>
                              <TableRow>
                                <TableCell>Visa Orders</TableCell>
                                <TableCell align="right">{report.countOfVisaOrders}</TableCell>
                                <TableCell align="right">{formatCurrency(Number(report.totalVisaOrders))}</TableCell>
                              </TableRow>
                              <TableRow>
                                <TableCell>Discounted Orders (all)</TableCell>
                                <TableCell align="right">{report.countOfCashOrdersWithDiscount + report.countOfVisaOrdersWithDiscount}</TableCell>
                                <TableCell align="right">{formatCurrency(Number(report.totalDiscountOfCashOrders + report.totalDiscountOfVisaOrders))}</TableCell>
                              </TableRow>
                              <TableRow>
                                <TableCell>Discounted Cash Orders</TableCell>
                                <TableCell align="right">{report.countOfCashOrdersWithDiscount}</TableCell>
                                <TableCell align="right">{formatCurrency(Number(report.totalDiscountOfCashOrders))}</TableCell>
                              </TableRow>
                              <TableRow>
                                <TableCell>Discounted Visa Orders</TableCell>
                                <TableCell align="right">{report.countOfVisaOrdersWithDiscount}</TableCell>
                                <TableCell align="right">{formatCurrency(Number(report.totalDiscountOfVisaOrders))}</TableCell>
                              </TableRow>
                              <TableRow>
                                <TableCell>Cancelled Cash Orders</TableCell>
                                <TableCell align="right">{report.countOfCashCancelledOrders}</TableCell>
                                <TableCell align="right">{formatCurrency(Number(report.totalCashCancelledOrders))}</TableCell>
                              </TableRow>
                              <TableRow>
                                <TableCell>Cancelled Visa Orders</TableCell>
                                <TableCell align="right">{report.countOfVisaCancelledOrders}</TableCell>
                                <TableCell align="right">{formatCurrency(Number(report.totalVisaCancelledOrders))}</TableCell>
                              </TableRow>
                              <TableRow>
                                <TableCell>Cancelled After Closed (Cash)</TableCell>
                                <TableCell align="right">{report.countOfCashCancelledAfterClosedOrders}</TableCell>
                                <TableCell align="right">{formatCurrency(Number(report.totalCashCancelledAfterClosedOrders))}</TableCell>
                              </TableRow>
                              <TableRow>
                                <TableCell>Cancelled After Closed (Visa)</TableCell>
                                <TableCell align="right">{report.countOfVisaCancelledAfterClosedOrders}</TableCell>
                                <TableCell align="right">{formatCurrency(Number(report.totalVisaCancelledAfterClosedOrders))}</TableCell>
                              </TableRow>
                              <TableRow>
                                <TableCell>Cancelled Before Close (Cash)</TableCell>
                                <TableCell align="right">{report.countOfCashCancelledBeforeClosedOrders}</TableCell>
                                <TableCell align="right">{formatCurrency(Number(report.totalCashCancelledBeforeClosedOrders))}</TableCell>
                              </TableRow>
                              <TableRow>
                                <TableCell>Cancelled Before Close (Visa)</TableCell>
                                <TableCell align="right">{report.countOfVisaCancelledBeforeClosedOrders}</TableCell>
                                <TableCell align="right">{formatCurrency(Number(report.totalVisaCancelledBeforeClosedOrders))}</TableCell>
                              </TableRow>
                            </TableBody>
                          </Table>
                        </Box>
                      </TableCell>
                    </TableRow>
                  )}
                </React.Fragment>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </div>
  );
};

export default DailyOrdersReport;